// src/lib/supabase/prompts.js
import { supabase } from "@/lib/supabase";

const TABLE_NAME = "prompts";

/**
 * @typedef {Object} Prompt
 * @property {string} id - UUID
 * @property {string} user_id - User UUID
 * @property {string} workspace_id - Workspace UUID
 * @property {string} title - Prompt title
 * @property {string} category - Prompt category
 * @property {string} description - Prompt description
 * @property {string} template - Template with variables
 * @property {string} content - Full content
 * @property {string[]} variables - Array of variable names
 * @property {string} icon - Icon identifier
 * @property {string} color - Color of the prompt
 */

/**
 * Create a new prompt
 * @param {Omit<Prompt, 'id'>} promptData
 * @returns {Promise<{ data: Prompt | null, error: Error | null }>}
 */
export async function createPrompt(promptData) {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .insert(promptData)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error("Error creating prompt:", error);
    return { data: null, error };
  }
}

/**
 * Get a prompt by ID
 * @param {string} id
 * @returns {Promise<{ data: Prompt | null, error: Error | null }>}
 */
export async function getPromptById(id) {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error("Error fetching prompt:", error);
    return { data: null, error };
  }
}

/**
 * Get prompts for a workspace
 * @param {string} workspaceId
 * @param {{ category?: string, search?: string, limit?: number, offset?: number }} options
 * @returns {Promise<{ data: Prompt[], error: Error | null, count: number }>}
 */
export async function getWorkspacePrompts(workspaceId, options = {}) {
  try {
    let query = supabase
      .from(TABLE_NAME)
      .select("*", { count: "exact" })
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: false });

    // Apply filters
    if (options.category) {
      query = query.eq("category", options.category);
    }

    if (options.search) {
      query = query.or(
        `title.ilike.%${options.search}%,description.ilike.%${options.search}%`
      );
    }

    // Apply pagination
    if (options.limit) {
      query = query.limit(options.limit);
    }

    if (options.offset) {
      query = query.range(
        options.offset,
        options.offset + (options.limit || 10) - 1
      );
    }

    const { data, error, count } = await query;

    if (error) throw error;

    return { data, error: null, count };
  } catch (error) {
    console.error("Error fetching workspace prompts:", error);
    return { data: [], error, count: 0 };
  }
}

/**
 * Update a prompt
 * @param {string} id
 * @param {Partial<Prompt>} updates
 * @returns {Promise<{ data: Prompt | null, error: Error | null }>}
 */
export async function updatePrompt(id, updates) {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error("Error updating prompt:", error);
    return { data: null, error };
  }
}

/**
 * Delete a prompt
 * @param {string} id
 * @returns {Promise<{ error: Error | null }>}
 */
export async function deletePrompt(id) {
  try {
    const { error } = await supabase.from(TABLE_NAME).delete().eq("id", id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error("Error deleting prompt:", error);
    return { error };
  }
}

/**
 * Duplicate a prompt
 * @param {string} id
 * @param {string} workspaceId - Optional new workspace ID
 * @returns {Promise<{ data: Prompt | null, error: Error | null }>}
 */
export async function duplicatePrompt(id, workspaceId = null) {
  try {
    // First get the prompt to duplicate
    const { data: promptToDuplicate, error: fetchError } =
      await getPromptById(id);

    if (fetchError) throw fetchError;
    if (!promptToDuplicate) throw new Error("Prompt not found");

    // Create new prompt data
    const newPromptData = {
      ...promptToDuplicate,
      id: undefined, // Remove id to let Supabase generate a new one
      title: `${promptToDuplicate.title} (Copy)`,
      workspace_id: workspaceId || promptToDuplicate.workspace_id,
      created_at: undefined, // Remove timestamps
      updated_at: undefined,
    };

    // Create the duplicate
    const { data, error } = await createPrompt(newPromptData);

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error("Error duplicating prompt:", error);
    return { data: null, error };
  }
}

/**
 * Get prompt variables from template
 * @param {string} template
 * @returns {string[]}
 */
export function extractPromptVariables(template) {
  if (!template) return [];

  const variableRegex = /\{\{([^}]+)\}\}/g;
  const matches = template.match(variableRegex) || [];

  return matches
    .map((match) => match.replace(/\{\{|\}\}/g, "").trim())
    .filter((value, index, self) => self.indexOf(value) === index);
}

/**
 * Validate prompt data
 * @param {Partial<Prompt>} promptData
 * @returns {{ isValid: boolean, errors: Object.<string, string> }}
 */
export function validatePromptData(promptData) {
  const errors = {};

  if (!promptData.title?.trim()) {
    errors.title = "Title is required";
  } else if (promptData.title.length < 3) {
    errors.title = "Title must be at least 3 characters";
  } else if (promptData.title.length > 100) {
    errors.title = "Title must be less than 100 characters";
  }

  if (!promptData.category) {
    errors.category = "Category is required";
  }

  if (!promptData.description?.trim()) {
    errors.description = "Description is required";
  }

  if (!promptData.content) {
    errors.content = "Content is required";
  }

  if (!promptData.user_id) {
    errors.user_id = "User is required";
  }

  if (!promptData.workspace_id) {
    errors.workspace_id = "Workspace is required";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
